import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { PartnerUnreadProvider } from './context/PartnerUnreadContext';
import Layout from './components/Layout';
import PartnerLayout from './components/PartnerLayout';
import Login from './pages/Login';
import Restaurants from './pages/Restaurants';
import RestaurantDetail from './pages/RestaurantDetail';
import AuditLogs from './pages/AuditLogs';
import AdminManagement from './pages/AdminManagement';
import Plans from './pages/Plans';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerRestaurantDetail from './pages/partner/PartnerRestaurantDetail';
import PartnerRequests from './pages/partner/PartnerRequests';
import AdminRequests from './pages/AdminRequests';

function PrivateRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <Loader />;
  return admin ? children : <Navigate to="/login" replace />;
}

function SuperadminRoute({ children }) {
  const { admin, loading, isSuperadmin } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/login" replace />;
  if (!isSuperadmin) return <Navigate to="/" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  const { admin, loading, isPartner } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/login" replace />;
  if (isPartner) return <Navigate to="/partner" replace />;
  return children;
}

function PartnerRoute({ children }) {
  const { admin, loading, isPartner } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/login" replace />;
  if (!isPartner) return <Navigate to="/" replace />;
  return children;
}

function Loader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text-muted)', fontSize: 14 }}>
      Loading…
    </div>
  );
}

function RoleRedirect() {
  const { admin, loading, isPartner } = useAuth();
  if (loading) return <Loader />;
  if (!admin) return <Navigate to="/login" replace />;
  if (isPartner) return <Navigate to="/partner" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  const { admin } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={admin ? <RoleRedirect /> : <Login />} />
      <Route path="/" element={<RoleRedirect />} />

      {/* Admin / Superadmin routes */}
      <Route path="/dashboard" element={
        <AdminOnlyRoute><Layout><Restaurants /></Layout></AdminOnlyRoute>
      } />
      <Route path="/restaurants/:id" element={
        <AdminOnlyRoute><Layout><RestaurantDetail /></Layout></AdminOnlyRoute>
      } />
      <Route path="/audit-logs" element={
        <AdminOnlyRoute><Layout><AuditLogs /></Layout></AdminOnlyRoute>
      } />
      <Route path="/admin-management" element={
        <SuperadminRoute><Layout><AdminManagement /></Layout></SuperadminRoute>
      } />
      <Route path="/plans" element={
        <SuperadminRoute><Layout><Plans /></Layout></SuperadminRoute>
      } />
      <Route path="/admin-requests" element={
        <AdminOnlyRoute><Layout><AdminRequests /></Layout></AdminOnlyRoute>
      } />

      {/* Partner routes - shared unread badge context */}
      <Route path="/partner" element={
        <PartnerUnreadProvider><PartnerRoute><PartnerLayout><PartnerDashboard /></PartnerLayout></PartnerRoute></PartnerUnreadProvider>
      } />
      <Route path="/partner/restaurants/:id" element={
        <PartnerUnreadProvider><PartnerRoute><PartnerLayout><PartnerRestaurantDetail /></PartnerLayout></PartnerRoute></PartnerUnreadProvider>
      } />
      <Route path="/partner/requests" element={
        <PartnerUnreadProvider><PartnerRoute><PartnerLayout><PartnerRequests /></PartnerLayout></PartnerRoute></PartnerUnreadProvider>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <SettingsProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </SettingsProvider>
  );
}
